"use client";

import { c as _c } from "react/compiler-runtime";
import ErrorComponent from "@/components/GlobalError";
import { Toaster } from "@/components/ui/Sonner";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { clerkAllowedRedirectOrigins } from "@/utils/clerk-redirect-origins";
import { normalizeClientRouteFamily } from "@/utils/client-route-family";
import { applyClientSentryAttribution, applyClientSentryAttributionToScope } from "@/utils/client-sentry-attribution";
import type { ClerkHostOptions } from "@/utils/workspace-hosts";
import { ClerkProvider } from "@clerk/nextjs";
import { ErrorBoundary } from "@sentry/nextjs";
import dynamic from "next/dynamic";
import { unstable_rethrow, usePathname } from "next/navigation";
import React, { useEffect } from "react";
import "./globals.css";
import { TimeAgoProvider } from "@/utils/date.client";
import { TRPCProvider } from "./providers";

// Modal focus isolation can modify a streamed progress node before hydration.
// This client-owned indicator has no server-visible content to preserve.
const NavigationFeedback = dynamic(() => import("@/components/navigation/NavigationFeedback").then(module => module.NavigationFeedback), {
  ssr: false
});
export default function RootLayoutClient(t0) {
  const $ = _c(11);
  const {
    children,
    clerkHostOptions
  } = t0;
  const pathname = usePathname();
  let t1;
  let t2;
  if ($[0] !== pathname) {
    t1 = () => {
      applyClientSentryAttribution({
        route: pathname,
        routeFamily: normalizeClientRouteFamily(pathname)
      });
    };
    t2 = [pathname];
    $[0] = pathname;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <React.Suspense fallback={null}><NavigationFeedback /></React.Suspense>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ErrorComponent />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Toaster />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children) {
    t6 = <TRPCProvider>{t5}{children}</TRPCProvider>;
    $[6] = children;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== clerkHostOptions || $[9] !== t6) {
    t7 = <TooltipProvider>{t3}<ErrorBoundary fallback={t4} beforeCapture={_temp}><TimeAgoProvider><ClerkProvider allowedRedirectOrigins={clerkAllowedRedirectOrigins} {...clerkHostOptions}>{t6}</ClerkProvider></TimeAgoProvider></ErrorBoundary></TooltipProvider>;
    $[8] = clerkHostOptions;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function _temp(scope, error) {
  unstable_rethrow(error);
  applyClientSentryAttributionToScope(scope, {
    errorSource: "client_render"
  });
}